import { api, getCsrf, getSessionToken } from "../api";
import { logLiveStep } from "./live-trace";

/** JT/T 1078 stream type 1 — sub stream. Live view asks for this instead of the main stream. */
export const LIVE_VIEW_STREAM_TYPE = 1;

const lastAt = new Map<string, number>();
const failStreak = new Map<string, number>();
const awaitingPublish = new Map<string, number>();
const inflight = new Set<string>();
const VIEWER_KEY = "dashcam:viewerId";

/** Match API floor; back off when a terminal never publishes so other cams stay smooth. */
export const STREAM_START_DEBOUNCE_MS = 4_000;
/** Same window as the API: a sent 0x9101 is not a failure until JT1078 misses this. */
export const STREAM_PUBLISH_GRACE_MS = 12_000;
const STREAM_START_MAX_MS = 60_000;

function slot(viewerId: string, deviceId: string, channel: number): string {
  return `${viewerId}\u0000${deviceId}\u0000${channel}`;
}

function backoffMs(key: string): number {
  const streak = failStreak.get(key) ?? 0;
  return Math.min(STREAM_START_DEBOUNCE_MS * 2 ** Math.min(streak, 4), STREAM_START_MAX_MS);
}

function cooldownMs(key: string, now: number): number {
  const backoff = backoffMs(key);
  const sent = awaitingPublish.get(key);
  if (sent == null) return backoff;
  const remain = STREAM_PUBLISH_GRACE_MS - (now - sent);
  if (remain > 0) return Math.max(backoff, remain);
  return backoff;
}

/** A start that never went live counts once, after the publish grace, not when 0x9101 is sent. */
function countMissedPublish(key: string, now: number): void {
  const sent = awaitingPublish.get(key);
  if (sent == null || now - sent < STREAM_PUBLISH_GRACE_MS) return;
  awaitingPublish.delete(key);
  failStreak.set(key, Math.min((failStreak.get(key) ?? 0) + 1, 6));
}

function clearPublishWait(key: string): void {
  failStreak.delete(key);
  awaitingPublish.delete(key);
}

function newViewerToken(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** Per-tab/app viewer lease id so web and mobile (or two tabs) do not share one lease. */
export function dashboardViewerId(): string {
  return viewerIdFor("live");
}

/** Separate lease per surface (dashboard, video wall, live trip) inside one tab. */
export function viewerIdFor(scope: string): string {
  const safeScope = scope.replace(/[^A-Za-z0-9._:-]+/g, "").slice(0, 24) || "live";
  const key = safeScope === "live" ? VIEWER_KEY : `${VIEWER_KEY}:${safeScope}`;
  try {
    const existing = sessionStorage.getItem(key) ?? "";
    if (existing && /^[A-Za-z0-9._:-]+$/.test(existing)) return existing;
    const id = newViewerToken();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return newViewerToken();
  }
}

export function streamStatusPath(deviceId: string, viewerId = dashboardViewerId()): string {
  return `/api/devices/${deviceId}/stream?viewerId=${encodeURIComponent(viewerId)}`;
}

/** One status read for the page. The server heartbeats each viewer's lease. */
export function streamStatusBatchPath(deviceIds: readonly string[], viewerId = dashboardViewerId()): string {
  return `/api/wall/streams?deviceIds=${encodeURIComponent(deviceIds.join(","))}&viewerId=${encodeURIComponent(viewerId)}`;
}

export function noteChannelLive(deviceId: string, channel: number, viewerId = dashboardViewerId()): void {
  clearPublishWait(slot(viewerId, deviceId, channel));
}

export function clearChannelStartCooldown(deviceId: string, channel: number, viewerId = dashboardViewerId()): void {
  const key = slot(viewerId, deviceId, channel);
  lastAt.delete(key);
  clearPublishWait(key);
}

export type ChannelStartMode = "0x9101" | "already_live" | "debounced" | "offline" | "cooldown" | "error";

/** One start per channel. A second call while this one is in flight does not open another session. */
export async function requestChannelStartDetail(
  deviceId: string,
  channel: number,
  viewerId = dashboardViewerId(),
  streamType?: number,
): Promise<{ ok: boolean; mode: ChannelStartMode }> {
  if (!deviceId) return { ok: false, mode: "error" };
  // Per viewer. A Dashboard start must not swallow the Video Wall's first POST,
  // or the wall never takes a lease and the camera is stopped out from under it.
  const key = slot(viewerId, deviceId, channel);
  if (inflight.has(key)) return { ok: false, mode: "cooldown" };
  const now = Date.now();
  countMissedPublish(key, now);
  if (now - (lastAt.get(key) ?? 0) < cooldownMs(key, now)) return { ok: false, mode: "cooldown" };
  inflight.add(key);
  lastAt.set(key, now);
  const began = now;
  logLiveStep({ vehicleId: deviceId, channel, step: "START_REQUEST" });
  try {
    const body: { channel: number; viewerId: string; streamType?: number } = { channel, viewerId };
    if (streamType === 0 || streamType === 1) body.streamType = streamType;
    const res = await api<{ ok?: boolean; mode?: string }>(`/api/devices/${deviceId}/stream/start`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    logLiveStep({
      vehicleId: deviceId,
      channel,
      step: "api_start",
      error: res?.mode === "offline" || res?.mode === "error" ? String(res.mode) : undefined,
      durationMs: Date.now() - began,
    });
    logLiveStep({
      vehicleId: deviceId,
      channel,
      step: "start_response",
      error: res?.mode,
      durationMs: Date.now() - began,
    });
    if (res?.mode === "already_live") {
      clearPublishWait(key);
      return { ok: true, mode: "already_live" };
    }
    if (res?.mode === "0x9101") {
      awaitingPublish.set(key, now);
      return { ok: true, mode: "0x9101" };
    }
    if (res?.mode === "sibling_wait" || res?.mode === "debounced") {
      // The command was not accepted as a new upload. Do not start the publish
      // grace, or this tile waits as if 0x9101 was written.
      return { ok: false, mode: "debounced" };
    }
    if (res?.mode === "offline") {
      lastAt.delete(key);
      awaitingPublish.delete(key);
      return { ok: false, mode: "offline" };
    }
    return { ok: Boolean(res?.ok), mode: res?.ok ? "0x9101" : "error" };
  } catch (err) {
    lastAt.delete(key);
    logLiveStep({
      vehicleId: deviceId,
      channel,
      step: "api_start",
      error: err instanceof Error ? err.message : "request_failed",
      durationMs: Date.now() - began,
    });
    return { ok: false, mode: "error" };
  } finally {
    inflight.delete(key);
  }
}

export async function requestChannelStart(
  deviceId: string,
  channel: number,
  viewerId = dashboardViewerId(),
  streamType = LIVE_VIEW_STREAM_TYPE,
): Promise<boolean> {
  const result = await requestChannelStartDetail(deviceId, channel, viewerId, streamType);
  return result.ok;
}

export async function requestChannelStop(
  deviceId: string,
  channel: number,
  opts?: { keepalive?: boolean; viewerId?: string },
): Promise<void> {
  if (!deviceId) return;
  clearChannelStartCooldown(deviceId, channel, opts?.viewerId ?? dashboardViewerId());
  const body = JSON.stringify({ channel, viewerId: opts?.viewerId ?? dashboardViewerId() });
  const path = `/api/devices/${deviceId}/stream/stop`;
  if (opts?.keepalive && typeof fetch === "function") {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const csrf = getCsrf();
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const session = getSessionToken();
    if (session) headers.Authorization = `Bearer ${session}`;
    try {
      void fetch(path, { method: "POST", body, credentials: "include", keepalive: true, headers });
    } catch {
      /* page is closing */
    }
    return;
  }
  try {
    await api(path, { method: "POST", body });
  } catch {
    /* best-effort release */
  }
}

export function releaseDeviceChannels(deviceId: string, opts?: { keepalive?: boolean; viewerId?: string }): void {
  void requestChannelStop(deviceId, 1, opts);
  void requestChannelStop(deviceId, 2, opts);
}

const holds = new Map<string, number>();

function holdKey(viewerId: string, deviceId: string, channel: number): string {
  return `${viewerId}:${deviceId}:${channel}`;
}

/** Local refcount so one surface cannot drop a lease another surface in this tab still holds. */
export function holdChannel(deviceId: string, channel: number, viewerId = dashboardViewerId()): void {
  const key = holdKey(viewerId, deviceId, channel);
  holds.set(key, (holds.get(key) ?? 0) + 1);
}

export function unholdChannel(
  deviceId: string,
  channel: number,
  viewerId = dashboardViewerId(),
  opts?: { keepalive?: boolean },
): void {
  const key = holdKey(viewerId, deviceId, channel);
  const next = (holds.get(key) ?? 0) - 1;
  if (next > 0) {
    holds.set(key, next);
    return;
  }
  holds.delete(key);
  void requestChannelStop(deviceId, channel, { ...opts, viewerId });
}
